import React, { useEffect, useState } from "react";
import { ExistingOrderDetailsContainer, OrderStatusChip, BarekamTextContainer } from "./styled";
import { useAppDispatch, useAppSelector } from "../../../../redux/store";
import { Recipient } from "../../../shared/models/user/recipient/recipient";
import Message from "../../../components/message";
import { MessageSections } from "../../../components/message/message";
import { OrderModel } from "../../../shared/models/order/order-item.model";
import { ClickAwayListener, Grid, IconButton, Tooltip } from "@mui/material";
import monthlyLimitService, { MonthlyLimitValidations } from "../../../../services/monthly-limits.service";
import BarekamText from "../../../components/text";
import { onOrderNameChange } from "../../../../services/orders.service";
import moment from "moment";
import BarekamButton, { SimpleButtons } from "../../../components/button";
import { BarekamIcon, IconVariants } from "../../../components/icon/barekam-icon";
import { ShippingMethodTextContainer } from "../shipping-method-selector/styled";
import { OrderPopupCardTitle } from "../styled";
import { EditOrderRecipientDialog } from "./edit-order-recipient-dialog/edit-order-recipient-dialog";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { getMessage } from "../../../shared/helper-functions";
import { getVolumeMultiplier } from "../../../calculate/calculate-functions";

const ExistingOrderDetails = React.memo((props: ExistingOrderDetailsProps) => {
    // TODO: refactor
    const { matchesPhone } = useAppSelector(state => state.deviceReducer);
    const { languageId, messageCache } = useAppSelector(state => state.messagesReducer);

    const { order, isReadonly, recipientChanged, recipient } = props;
    const { allRecipients } = useAppSelector(state => state.recipientDataReducer);

    const orderTrackMethod: string = order.trackRecord[0].state.name;
    const [orderWeight, setOrderWeight] = React.useState<number>();
    const [orderCapacity, setOrderCapacity] = React.useState<number>();
    const [popupOpened, setPopupOpened] = React.useState(false);
    const [monthlyLimitValidations, setMonthlyLimitValidations] = useState<MonthlyLimitValidations>({});
    const [open, setOpen] = useState(false);

    const dispatch = useAppDispatch();

    useEffect(() => {
        order.recipient && checkMonthlyLimits(order.recipient)
    }, [order.products]
    )

    useEffect(() => {
        if (order.weight) {
            setOrderWeight(order.weight);
        } else {
            setOrderWeight(order.products.reduce((overallWeight, product) => {
                if (product.weightUnit && product.weightUnit.weightUnitId !== order.weightUnit?.weightUnitId) {
                    return product.weightUnit.weightUnitId === 1
                        ? overallWeight + Math.ceil((product.weight || 0) * 2.2046)
                        : overallWeight + Math.ceil((product.weight || 0) / 2.2046)
                }
                return overallWeight + (product.weight || 0);
            }, 0));
        }
    }, [order.products, order.weight])

    useEffect(() => {
        if (order.height && order.width && order.length) {
            const sum = getVolumeMultiplier((+order.width) * (+order.height) * (+order.length), 1)
            setOrderCapacity(Math.floor(sum * 10) / 10);
        }
    }, [order.height, order.width, order.length])

    function handleRecipientChange(recipient: Recipient | undefined) {
        if (recipient) {
            recipientChanged(recipient);
        }

        setPopupOpened(false);
    }

    function onEditRecipientClicked() {
        setPopupOpened(true)
    }

    function checkMonthlyLimits(orderRecipient: Recipient) {
        const monthlyLimit = allRecipients.find(recipient => recipient.recipientId === orderRecipient.recipientId)?.monthlyLimit;

        if (monthlyLimit) {
            const {
                overallCount,
                overallWeight,
                overallPrice
            } = monthlyLimitService.getOrderOverallCounts(monthlyLimit, order.products);

            setMonthlyLimitValidations({
                exceedsByCount: monthlyLimit?.pieceCountLimitPerRecipient ? overallCount > monthlyLimit.pieceCountLimitPerRecipient : false,
                exceedsByWeight: monthlyLimit?.weightLimitPerParcel ? overallWeight > monthlyLimit.weightLimitPerParcel : false,
                exceedsByPrice: false // TODO: implement price validation
            })
        }
    }

    function orderNameChange(value: string) {
        onOrderNameChange(value, dispatch);
    }

    return (
        <ExistingOrderDetailsContainer container columnSpacing={'1rem'} rowSpacing={'1rem'}>
            <Grid item xs={12}>
                <OrderPopupCardTitle>
                    <BarekamText variant={'m'} bold>
                        <Message messageKey={'existing_order_details'} sectionId={MessageSections.ORDERS} />
                    </BarekamText>
                </OrderPopupCardTitle>
            </Grid>
            <Grid item xs={matchesPhone ? 12 : 6} gap={'0.25rem'} alignItems={'center'}>
                <BarekamText variant={"m"} width={'unset'} overflow={'unset'} opaque>
                    <Message messageKey={'bls_tracking_number'} sectionId={MessageSections.ORDERS} />:
                </BarekamText>

                <BarekamText variant={"m"} overflow={'hidden'}>
                    {order.internalTrackingNumber}
                </BarekamText>
            </Grid>

            <Grid item xs={matchesPhone ? 12 : 6} gap={'0.25rem'} alignItems={'center'}>
                <BarekamText variant={"m"} width={'unset'} overflow={'unset'} opaque>
                    <Message messageKey={'expected_on'} sectionId={MessageSections.ORDERS} />
                </BarekamText>

                <BarekamText variant={"m"} overflow={'hidden'}>
                    {moment(order.expectedDate).format('DD/MM/YYYY')}
                </BarekamText>
            </Grid>

            {orderWeight
                ? <Grid item xs={matchesPhone ? 12 : 6} gap={'0.25rem'} alignItems={'center'}>
                    <BarekamText variant={"m"} width={'unset'} overflow={'unset'} opaque>
                        <Message messageKey={'weight'} sectionId={MessageSections.ORDERS} />:
                    </BarekamText>

                    <BarekamText variant={"m"} overflow={'hidden'}>
                        {orderWeight}
                        {' '}
                        {order.weightUnit?.name[languageId]}
                    </BarekamText>
                </Grid>
                : <></>
            }

            {orderCapacity && (
                <Grid item xs={matchesPhone ? 12 : 6} gap={'0.25rem'} alignItems={'center'}>
                    <BarekamText variant={"m"} width={'unset'} overflow={'unset'} opaque>
                        <Message messageKey={'order_capacity'} sectionId={MessageSections.ORDERS} />:
                    </BarekamText>

                    <BarekamTextContainer>
                        <BarekamText style={{marginRight: 4}} variant={"m"}>{orderCapacity}</BarekamText>
                        <BarekamText variant={"m"} width={'unset'} overflow={'unset'} opaque>
                            <Message messageKey={'kg'} sectionId={MessageSections.MAIN} />
                        </BarekamText>
                        <ClickAwayListener onClickAway={() => setOpen(false)}>
                            <Tooltip title={getMessage('order_capacity_info', MessageSections.ORDERS, messageCache, languageId)} open={open} arrow>
                                <IconButton size="small" onClick={() => setOpen(prev => !prev)}>
                                    <InfoOutlinedIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </ClickAwayListener>
                    </BarekamTextContainer>
                </Grid>
            )}

            <Grid item xs={matchesPhone ? 12 : 6} gap={'0.25rem'} alignItems={'center'}>
                <BarekamText variant={"m"} width={'unset'} overflow={'unset'} opaque>
                    <Message messageKey={'status'} sectionId={MessageSections.ORDERS} />:
                </BarekamText>

                <OrderStatusChip label={<Message messageKey={orderTrackMethod} sectionId={MessageSections.ORDERS} />} />
            </Grid>

            <Grid item xs={matchesPhone ? 12 : 6} gap={'0.25rem'} alignItems={'center'}>
                <BarekamText variant={"m"} width={'unset'} overflow={'unset'} opaque>
                    <Message messageKey={'recipient'} sectionId={MessageSections.ORDERS} />:
                </BarekamText>

                <BarekamText variant={"m"} overflow={'hidden'}>
                    {recipient
                        ? `${recipient?.firstName} ${recipient?.lastName}`
                        : `${order?.recipient?.firstName} ${order?.recipient?.lastName}`
                    }
                </BarekamText>

                {isReadonly
                    ? ''
                    : <BarekamButton type={SimpleButtons.LINK} onClick={() => onEditRecipientClicked()}>
                        <BarekamIcon variant={IconVariants.edit} />
                    </BarekamButton>
                }
            </Grid>

            {order.paymentInfo?.shippingAmount
                ? <Grid item xs={matchesPhone ? 12 : 6} gap={'0.25rem'} alignItems={'center'}>
                    <BarekamText variant={"m"} width={'unset'} overflow={'unset'} opaque>
                        <Message messageKey={'payment'} sectionId={MessageSections.ORDERS} />:
                    </BarekamText>

                    <BarekamText variant={"m"} overflow={'hidden'}>
                        {order.paymentInfo?.shippingAmount}
                        {' '}
                        <Message messageKey={'amd'} sectionId={MessageSections.ORDERS} />
                    </BarekamText>
                </Grid>
                : <></>
            }

            <Grid item xs={matchesPhone ? 12 : 6} gap={'0.25rem'} alignItems={'center'}>
                <BarekamText variant={"m"} width={'unset'} overflow={'unset'} opaque>
                    <Message messageKey={'country'} sectionId={MessageSections.ORDERS} />:
                </BarekamText>

                <BarekamIcon variant={IconVariants.flag} country={order.shippingInfo.country?.abbreviation.toLowerCase()} />

                <BarekamText variant={'m'}>{order.shippingInfo.country?.name[languageId]} ({order.shippingInfo.country?.abbreviation})</BarekamText>
            </Grid>

            {order.paymentInfo?.tax
                ? <Grid item xs={matchesPhone ? 12 : 6} gap={'0.25rem'} alignItems={'center'}>
                    <BarekamText variant={"m"} width={'unset'} overflow={'unset'} opaque>
                        <Message messageKey={'taxes'} sectionId={MessageSections.ORDERS} />:
                    </BarekamText>

                    <BarekamText variant={"m"} overflow={'hidden'}>
                        {order.paymentInfo?.tax}
                        {' '}
                        <Message messageKey={'amd'} sectionId={MessageSections.ORDERS} />
                    </BarekamText>
                </Grid>
                : <></>
            }

            <Grid item xs={matchesPhone ? 12 : 6} gap={'0.25rem'} alignItems={'center'}>
                <BarekamText variant={"m"} width={'unset'} overflow={'unset'} opaque>
                    <Message messageKey={'shipping_method'} sectionId={MessageSections.ORDERS} />:
                </BarekamText>

                <BarekamIcon variant={IconVariants.shipping}
                    shippingMethod={order.shippingInfo.shippingMethod.name.toLowerCase()} />

                <ShippingMethodTextContainer>
                    <BarekamText variant={'m'} style={{ height: '1.2rem' }}>
                        <Message messageKey={order.shippingInfo.shippingMethod.name} sectionId={MessageSections.ORDERS} />
                    </BarekamText>

                    <BarekamText variant={'s'} opaque align={'center'}>
                        <Message messageKey={order.shippingInfo.shippingMethod.description} sectionId={MessageSections.ORDERS} />
                    </BarekamText>
                </ShippingMethodTextContainer>
            </Grid>

            {monthlyLimitValidations.exceedsByPrice
                ? <>
                    <Grid item xs={matchesPhone ? 12 : 6} />
                    <Grid item xs={matchesPhone ? 12 : 6} gap={'0.25rem'} alignItems={'center'}>
                        <BarekamText variant={"xs"}>
                            <Message messageKey={'recipient_exceeds_by_price'} sectionId={MessageSections.ORDERS} />
                        </BarekamText>
                    </Grid>
                </>
                : <></>
            }
            {monthlyLimitValidations.exceedsByWeight
                ? <>
                    <Grid item xs={matchesPhone ? 12 : 6} />
                    <Grid item xs={matchesPhone ? 12 : 6} gap={'0.25rem'} alignItems={'center'}>
                        <BarekamText variant={"xs"}>
                            <Message messageKey={'recipient_exceeds_by_weight'} sectionId={MessageSections.ORDERS} />
                        </BarekamText>
                    </Grid>
                </>
                : <></>
            }
            {monthlyLimitValidations.exceedsByCount
                ? <>
                    <Grid item xs={matchesPhone ? 12 : 6} />
                    <Grid item xs={matchesPhone ? 12 : 6} gap={'0.25rem'} alignItems={'center'}>
                        <BarekamText variant={"xs"}>
                            <Message messageKey={'recipient_exceeds_by_count'} sectionId={MessageSections.ORDERS} />
                        </BarekamText>
                    </Grid>
                </>
                : <></>
            }
            <EditOrderRecipientDialog key={'new-recipient-dialog'}
                selectedRecipient={order.recipient}
                open={popupOpened}
                onClose={(recipient) => handleRecipientChange(recipient)} />
        </ExistingOrderDetailsContainer>
    )
})

export default ExistingOrderDetails;

interface ExistingOrderDetailsProps {
    order: OrderModel,
    recipient: Recipient | undefined,
    recipientChanged: (recipient: Recipient) => void,
    isReadonly?: boolean,
}
